import React from 'react';
import moment from 'moment';

import { formatWorkOrderMilestoneName } from 'libs/formatWorkOrderMilestoneName';
import {
  StyledMilestoneContainer,
  StyledMilestoneParagraph,
  StyledButterContent,
  StyledInquiryCodesTag,
} from './katal-styles';
import type { WorkOrderInquiry } from '@xbcb/api-gateway-client';
import { WorkOrderMilestoneName } from '@xbcb/work-order-types';
import { codeToText } from '@xbcb/js-utils';
import { useCurrentUser } from 'libs/hooks';
import { DataCyPrefix, createDataCyValue } from '@xbcb/ui-types';
import { useBundle } from '@amzn/react-arb-tools';
import { safeGetMessage } from '@xbcb/ui-utils';

enum MilestoneSource {
  CBMS = 'CBMS',
  SYSTEM = 'SYSTEM',
}

const getTimestamp = (iso: string) =>
  moment(iso).format('MMM D YYYY, h:mm a zz');

interface MilestoneProps {
  isOpen?: boolean;
  milestone: any;
  toggleOpen: any;
  inquiries?: WorkOrderInquiry[];
  customContent?: React.ReactNode;
}

const MilestoneKatal = ({
  isOpen,
  milestone,
  toggleOpen,
  inquiries,
  customContent,
}: MilestoneProps) => {
  const [milestoneNameBundle] = useBundle(
    'types.workOrder.enums.WorkOrderMilestoneName',
  );
  const [inquiryReasonCodeBundle] = useBundle(
    'types.workOrder.enums.WorkOrderInquiryReasonCode',
  );
  const { accountType } = useCurrentUser();
  const { id: milestoneId, name, occurenceTime, source, created } = milestone;
  const formattedMilestoneName = formatWorkOrderMilestoneName(
    name,
    accountType,
    'capital',
  );
  const milestoneName = safeGetMessage(
    milestoneNameBundle,
    name.toLowerCase(),
    undefined,
    formattedMilestoneName,
  );
  const clientName = created?.client?.name;
  const sourceName =
    source && source !== MilestoneSource.CBMS
      ? source
      : clientName
      ? clientName
      : MilestoneSource.SYSTEM;

  const butterKey = `milestone_${formatWorkOrderMilestoneName(
    name,
    accountType,
  )}`;

  const handleClick = () => {
    toggleOpen(milestoneId);
  };

  let inquiryReasonCodes: string[] = [];
  if (
    inquiries &&
    milestone.name === WorkOrderMilestoneName.WORK_ORDER_PENDING_INQUIRY
  ) {
    const inquiry = inquiries.find(
      (inquiry) => inquiry.milestone.id === milestone.id,
    );
    if (inquiry) {
      inquiryReasonCodes = inquiry.reasonCodes ?? [];
    }
  }

  const reasonCodeTags = inquiryReasonCodes.map((code, index) => (
    <StyledInquiryCodesTag key={index}>
      {safeGetMessage(
        inquiryReasonCodeBundle,
        code.toLowerCase(),
        undefined,
        codeToText(code),
      )}
    </StyledInquiryCodesTag>
  ));

  return (
    <StyledMilestoneContainer onClick={handleClick} $isOpen={isOpen}>
      <StyledMilestoneParagraph
        $bold
        data-cy={createDataCyValue(DataCyPrefix.MILESTONE_NAME, name)}
      >
        {milestoneName}
      </StyledMilestoneParagraph>
      {occurenceTime && (
        <StyledMilestoneParagraph $fontSize="12px">
          <span>
            <i>{getTimestamp(occurenceTime)}</i>
          </span>
        </StyledMilestoneParagraph>
      )}
      <StyledMilestoneParagraph>{sourceName}</StyledMilestoneParagraph>
      {isOpen && <StyledButterContent whiteSpinner={false} code={butterKey} />}
      {reasonCodeTags.length > 0 && (
        <StyledMilestoneParagraph $fontSize="12px">
          {reasonCodeTags}
        </StyledMilestoneParagraph>
      )}
      {customContent && (
        <StyledMilestoneParagraph $fontSize="12px">
          {customContent}
        </StyledMilestoneParagraph>
      )}
    </StyledMilestoneContainer>
  );
};

export default MilestoneKatal;
